import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, CheckCircle, Send, Sparkles, Pencil } from 'lucide-react';

function useButtonKeyboardNav(count, callbacks) {
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        setSelected(prev => Math.min(prev + 1, count - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        setSelected(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        callbacks[selected]?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [count, selected, callbacks]);

  return selected;
}

const AskClarifyStage = (props) => {
  const callbacks = useCallback(() => [props.onYesMeantSomethingElse, props.onNoThisIsWhatIMeant], [props.onYesMeantSomethingElse, props.onNoThisIsWhatIMeant]);
  const selected = useButtonKeyboardNav(2, callbacks());

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
      className="gm-vague-row"
    >
      <div className="gm-vague-btns">
        <button
          onClick={props.onYesMeantSomethingElse}
          className={`gm-vague-pill ${selected === 0 ? 'gm-vague-pill--focused' : 'gm-vague-pill--primary'}`}
        >
          <RefreshCw className="gm-icon-xs" />
          Yes, let me rephrase
        </button>
        <button
          onClick={props.onNoThisIsWhatIMeant}
          className={`gm-vague-pill ${selected === 1 ? 'gm-vague-pill--focused' : 'gm-vague-pill--default'}`}
        >
          No, this is what I meant
        </button>
      </div>
    </motion.div>
  );
};

const SuggestRefinedStage = (props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(props.refinedQuestion);
  const textareaRef = useRef(null);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedText(props.refinedQuestion);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleAccept = () => {
    if (isEditing && editedText.trim() !== props.refinedQuestion) {
      props.onAccept(editedText.trim());
    } else {
      props.onAccept();
    }
  };

  const buttonCount = props.onEditQuestion ? 3 : 2;
  const callbacks = useCallback(
    () => props.onEditQuestion
      ? [handleAccept, props.onEditQuestion, props.onRephrase]
      : [handleAccept, props.onRephrase],
    [handleAccept, props.onEditQuestion, props.onRephrase]
  );
  const selected = useButtonKeyboardNav(buttonCount, callbacks());

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
      className="gm-vague-suggest"
    >
      <div className="gm-vague-refined-box">
        <Sparkles className="gm-icon-sm gm-text-primary gm-vague-refined-icon" />
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={e => setEditedText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAccept(); } }}
            className="gm-vague-refined-textarea"
            rows={2}
          />
        ) : (
          <p
            className="gm-vague-refined-text"
            onClick={handleEditClick}
            title="Tap to edit"
          >
            {props.refinedQuestion}
          </p>
        )}
      </div>
      {!isEditing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="gm-vague-edit-hint"
        >
          <span>tap to edit ↑</span>
        </motion.div>
      )}
      <div className="gm-vague-btns">
        <button
          onClick={handleAccept}
          className={`gm-vague-pill gm-vague-pill--accept ${selected === 0 ? 'gm-vague-pill--ring' : ''}`}
        >
       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M13.3333 4L5.99996 11.3333L2.66663 8" stroke="#212121" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          {isEditing ? 'Use edited' : 'Use this'}
        </button>
        {props.onEditQuestion && (
          <button
            onClick={props.onEditQuestion}
            className={`gm-vague-pill ${selected === 1 ? 'gm-vague-pill--focused' : 'gm-vague-pill--default'}`}
          >
       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#656565" stroke-width="1.25714" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.2453 1.75015C12.5105 1.48493 12.8702 1.33594 13.2453 1.33594C13.6203 1.33594 13.98 1.48493 14.2453 1.75015C14.5105 2.01537 14.6595 2.37508 14.6595 2.75015C14.6595 3.12522 14.5105 3.48493 14.2453 3.75015L8.23659 9.75948C8.07829 9.91765 7.88273 10.0334 7.66793 10.0962L5.75259 10.6562C5.69523 10.6729 5.63442 10.6739 5.57653 10.6591C5.51865 10.6442 5.46581 10.6141 5.42356 10.5719C5.38131 10.5296 5.35119 10.4768 5.33636 10.4189C5.32153 10.361 5.32253 10.3002 5.33926 10.2428L5.89926 8.32748C5.96228 8.11285 6.07828 7.91752 6.23659 7.75948L12.2453 1.75015Z" stroke="#656565" stroke-width="1.25714" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
            Edit
          </button>
        )}
        <button
          onClick={props.onRephrase}
          className={`gm-vague-pill ${selected === (props.onEditQuestion ? 2 : 1) ? 'gm-vague-pill--focused' : 'gm-vague-pill--default'}`}
        >
          <RefreshCw className="gm-icon-xs" />
          Use my original
        </button>
      </div>
    </motion.div>
  );
};

const ExplainManualStage = (props) => {
  const hasGoBack = !!props.onGoBack;
  const count = hasGoBack ? 2 : 1;
  const callbacks = useCallback(
    () => hasGoBack ? [props.onUnderstandManual, props.onGoBack] : [props.onUnderstandManual],
    [props.onUnderstandManual, props.onGoBack, hasGoBack]
  );
  const selected = useButtonKeyboardNav(count, callbacks());

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
      className="gm-vague-manual-row"
    >
      <button
        onClick={props.onUnderstandManual}
        className={`gm-vague-pill ${selected === 0 ? 'gm-vague-pill--focused' : 'gm-vague-pill--primary'}`}
      >
        <CheckCircle className="gm-icon-xs" />
        I understand, continue
      </button>
      {hasGoBack && (
        <button
          onClick={props.onGoBack}
          className={`gm-vague-pill ${selected === 1 ? 'gm-vague-pill--focused' : 'gm-vague-pill--default'}`}
        >
          <RefreshCw className="gm-icon-xs" />
          Go back
        </button>
      )}
    </motion.div>
  );
};

export const VagueQuestionPrompt = (props) => {
  const [inputValue, setInputValue] = useState('');

  if (props.stage === 'ask-clarify') {
    return <AskClarifyStage {...props} />;
  }

  if (props.stage === 'asking-details') {
    const handleSubmit = () => {
      if (inputValue.trim()) {
        props.onAnswer(inputValue.trim());
        setInputValue('');
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 350, damping: 25 }}
        className="gm-vague-details"
      >
        <div className="gm-vague-details-row">
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Type your answer..."
            className="gm-vague-details-input"
            autoFocus
          />
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="gm-vague-details-send"
          >
            <Send className="gm-icon-sm" />
          </button>
        </div>
      </motion.div>
    );
  }

  if (props.stage === 'suggest-refined') {
    return <SuggestRefinedStage {...props} />;
  }

  return <ExplainManualStage {...props} />;
};
